import Sugar from 'sugar';
import { html, css, nothing } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { consume } from '@lit/context';
import { ltnToastServiceContext } from '../../../../ns-services/contexts.js';

import { LtnToastService } from '../../../../ltn-toast/ltn-toast-service.js';

import { callLambda } from '../../../../helpers/lambda.js';

import NSSpreadsheet from '../spreadsheet/base.js';

import JobSchema from '../../../../models/job-schema.js';
import Company from '../../../../models/company.js';
import Invitation from '../../../../models/invitation.js';

import { getActionFromLambdaResults } from '../../../../helpers/lambda.js';
import { LiveAnnouncer } from '../../../../helpers/live-announcer.js';
import '../../../../ns-elements/ns-job-layout.js';
import '../../../../ns-elements/ns-upload.js';
import definition from '@job-schemas/company/invite-company-employees.json';
import { jobSchema } from '../../job-schema.js';

interface Row {
  'Employee Name': string;
  'Employee Email': string;
  'Employee Job Title': string;
}

@customElement(NSInviteCompanyEmployees.is)
export default class NSInviteCompanyEmployees extends NSSpreadsheet {
  static get is() {
    return 'ns-invite-company-employees';
  }

  @consume({ context: ltnToastServiceContext })
  private __toast?: LtnToastService;

  @state()
  private __company?: Company;

  // The base class keeps its announcer private, and this card renders its own layout
  private __liveAnnouncer = new LiveAnnouncer(this);

  public announce(message: string) {
    this.__liveAnnouncer.announce(message);
  }

  static get schema(): JobSchema {
    return jobSchema(definition, { outcomes: { complete: { type: 'hidden' } } });
  }

  static styles = [
    ...super.styles,
    css`
      .message--error {
        background-color: var(--ns-theme-red);
        color: var(--ns-theme-black);
      }
      .message--warning {
        background-color: var(--ns-theme-amber);
        color: var(--ns-theme-black);
      }
      .message--success {
        background-color: var(--ns-theme-green);
        color: var(--ns-theme-black);
      }

      .messages {
        padding: 0;
        list-style: none;
      }

      .job-panel .stat {
        background-color: var(--wa-color-surface-default);
      }
    `,
  ];

  async connectedCallback() {
    try {
      // No role column: invitees join as Staff, and are given roles after (D45)
      this._columns = ['Employee Name', 'Employee Email', 'Employee Job Title'];
      this._dataValidationRules = {
        'Employee Name': ['required'],
        'Employee Email': ['required', 'email', 'unique'],
        'Employee Job Title': ['optional'],
      };

      await super.connectedCallback();
      await this.updateComplete;

      if (!this._db) throw new Error('Unable to find db service');

      if (!this._directory) throw new Error('Unable to find NSDirectoryService');

      if (!this._auth) throw new Error('Unable to find auth service');

      if (!this.__toast) throw new Error('Unable to find toast service');

      await this._directory.initialised;
      const profile = this._directory.me;
      if (!profile || !this._directory.can('PEOPLE_INVITE')) {
        throw new Error('The user does not have the permission to invite anyone to nodeStream');
      }

      this.__company = this._directory.company;
      this.importantMessage = `Download the template, fill in your employee details,
        and upload the file to validate and import your data. Once confirmed,
        each employee will automatically receive an invitation to onboard.
        Note that the file needs to be csv format.`;
    } catch (err: unknown) {
      this.failJob(err, 'Unexpected error occurred while loading companies');
    }
  }

  protected async _upload() {
    if (!this._job) throw new Error('No job available');
    if (!this._workflowService) throw new Error('No workflow service available');

    if (!this._auth) throw new Error('Missing auth service');
    if (!this._db) throw new Error('Unable to find db service');
    if (!this._directory) throw new Error('No directory service available');
    if (!this._directory.me) throw new Error('Missing dir service person');

    const profile = this._directory.me;
    if (!profile) throw new Error('Unable to find user profile from directory service');

    const company = this._directory.company;
    if (!company) throw new Error('Unable to fetch active company from directory service');

    const workflowSchema = await this._workflowService.findWorkflowSchema('@ns/accept-company-invitation');
    if (!workflowSchema) throw new Error('Unable to find workflow schema, @ns/accept-company-invitation:1.0.0');

    const inviterEmail = profile.email;
    if (!inviterEmail) throw new Error('Unable to find current users email to send invite.');

    if (await this._validate()) {
      let reader: ReadableStreamDefaultReader<any>;

      try {
        reader = this._getInputFileStream();
      } catch (err) {
        this._handleInputFileStreamError(err);
        return;
      }

      try {
        this.loading('Importing Data');

        const output: Invitation[] = [];
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          const row: Row = value;

          // Invitation Data
          const inviteeName = row['Employee Name'];
          const inviteeEmail = row['Employee Email'];
          const inviteeCompanyName = company.name;

          const invitation = this._db.createObject<Invitation>('invitation');

          invitation.inviter.personId = profile.id;
          invitation.inviter.email = inviterEmail;
          invitation.inviter.companyId = company.id;

          invitation.invitee.name = inviteeName;
          invitation.invitee.email = inviteeEmail;
          invitation.invitee.company.name = inviteeCompanyName;
          invitation.invitee.jobTitle = row['Employee Job Title'];
          invitation.invitee.company.id = company.id;
          invitation.expiryDate = Sugar.Date.create('in 7 days');

          const existingCount = await this._db.count('invitation', {
            'invitee.email': inviteeEmail,
            'invitee.company.name': inviteeCompanyName,
          });

          if (existingCount > 0) {
            this.addMessage(
              'warning',
              `Skipping invitation for ${inviteeName} from ${inviteeCompanyName} as it already exists`,
            );
            continue;
          }

          output.push(invitation);
        }

        // Chunking the data to a 100 per a lambda call
        const invitations = this.__splitArrayIntoChunks(output);
        const totalToImport = output.length;

        for await (const invitation of invitations) {
          const output = await callLambda(
            'onboarding/create-invitation',
            'POST',
            this._auth.token,
            JSON.stringify({
              userId: profile.userId,
              personId: profile.id,
              oauthApp: sessionStorage.getItem('oauthApp'),
              companyId: company.id,
              workflowParentId: this._job.workflow.id,
              invitations: invitation,
            }),
          );
          const res = output.res as unknown as { invitations: Invitation[] };

          for await (const inv of res.invitations) {
            const results = await this._workflowService.createWorkflowFromSchema(
              workflowSchema,
              'launcher',
              { COMPANY_ID: company.id, INVITATION_ID: inv.id },
              {
                name: `${company.name} invited you as one of its employees`,
                parentId: this._job.workflow.id,
              },
            );

            // Make sure the lambda returned that the workflow was created.
            getActionFromLambdaResults(results, 'workflow_created');
            this._countImport = this._countImport + 1;
          }
          this.announce(`${this._countImport} of ${totalToImport} invitations imported so far.`);
        }
        this.loaded();
        this.__toast?.queueToast({
          label: `${output.length} invitations generated and currently sending emails out.`,
          timeout: 4000,
        });
        this.outcome('complete');
      } catch (err) {
        this.loaded();
        this.failJob(err, 'Unexpected error occurred while loading companies');
      }
    }
  }

  private __splitArrayIntoChunks(arr: Invitation[], chunkSize: number = 100) {
    return Array.from({ length: Math.ceil(arr.length / chunkSize) }, (v, i) =>
      arr.slice(i * chunkSize, i * chunkSize + chunkSize),
    );
  }

  render() {
    return html`
      <ns-job-layout>
        ${this.__liveAnnouncer.render()}
        <div class="job-panel">
          <h3>Import</h3>
          <p>
            <strong>Step 1.</strong>
            Download the following spreadsheet and fill in the company data that you'd like to import.
          </p>
          <a id="templateLink" download="template.csv" href="#"><div class="pill">Download Template</div></a>
          <p>
            <strong>Step 2.</strong>
            Select the spreadsheet, validate then import it.
          </p>
          <ns-upload
            id="fileUpload"
            label="Spreadsheet file"
            @upload-outcome=${(e: CustomEvent) => (e.detail.error ? this.showError(e.detail.error) : this.clearError())}
          ></ns-upload>
        </div>
        <div class="job-panel">
          <h3>Overview</h3>
          <div class="stats" role="list">
            <div class="stat stat--green" role="listitem">
              <div class="stat__label">Valid</div>
              <div class="stat__number">${this._countValid}</div>
            </div>
            <div class="stat stat--red" role="listitem">
              <div class="stat__label">Failed</div>
              <div class="stat__number">${this._countFailed}</div>
            </div>
            <div class="stat stat--red" role="listitem">
              <div class="stat__label">Imported</div>
              <div class="stat__number">${this._countImport}</div>
            </div>
          </div>
        </div>
        ${this._messages.length > 0
          ? html`
              <div class="job-panel">
                <h3>Import Information</h3>
                <ul class="messages">
                  ${this._messages.map(
                    (message) => html`
                      <li class="message message--${message.type}">${message.text}</li>
                    `,
                  )}
                </ul>
              </div>
            `
          : nothing}
        ${this.importantMessage
          ? html`
              <div slot="aside" class="primary-info">
                <span class="info-icon" aria-hidden="true">i</span>
                <h3>Important Information</h3>
                <p>${this.importantMessage}</p>
              </div>
            `
          : nothing}
      </ns-job-layout>
    `;
  }

  renderActions() {
    return html`
      <wa-button variant="brand" appearance="outlined" type="button" @click=${() => this._validate()}>
        Validate
      </wa-button>
      <wa-button variant="brand" type="button" .disabled=${!this._canImport} @click=${() => this._upload()}>
        Import
      </wa-button>
    `;
  }
}
